import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActiveCartService } from '@cart/data-access';
import { ValidationErrorKeys } from '@checkout/const';
import { CheckoutContactFormService, CheckoutValidationService, CustomCheckoutService } from '@checkout/feature';
import { ExtendedCustomFormValidators } from '@shared/helpers';
import { DeviceService } from '@shared/services';
import { ScrollToService } from '@shared/v2';
import { UserAccountFacade } from '@spartacus/user';
import { User } from '@user/const';
import { Subscription, filter } from 'rxjs';

@Component({
  selector: 'sd-checkout-contacts',
  templateUrl: './checkout-contacts.component.html',
  styleUrls: ['./checkout-contacts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SdvorCheckoutContactsComponent implements OnInit, OnDestroy {
  form: FormGroup;

  isMobile$ = this.deviceService.isMobileSmDown$;
  isSubmitting$ = this.formService.isSubmitting$;
  placeOrderError$ = this.customCheckoutService.getPlaceOrderError();

  protected subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private userAccountService: UserAccountFacade,
    public formService: CheckoutContactFormService,
    protected customCheckoutService: CustomCheckoutService,
    private deviceService: DeviceService,
    protected activeCartService: ActiveCartService,
    private viewRef: ViewContainerRef,
    private scrollToService: ScrollToService,
    private checkoutValidationService: CheckoutValidationService,
    private cdr: ChangeDetectorRef,
  ) {
    const values = this.formService.formValues$.value;
    this.form = this.fb.group({
      phone: [values.phone, [Validators.required, Validators.minLength(10)]],
      addPhone: [values.addPhone, [Validators.minLength(10)]],
      email: [values.email, [ExtendedCustomFormValidators.emailValidator, Validators.maxLength(255)]],
      comment: [values.comment, [Validators.maxLength(255)]],
      callMe: values.callMe,
    });
  }

  ngOnInit(): void {
    this.connectToFormService();

    this.subscription.add(
      this.isSubmitting$.subscribe((isSubmitting) => {
        const action = isSubmitting ? 'disable' : 'enable';
        this.form[action]();
      }),
    );

    this.subscription.add(
      this.userAccountService.get().subscribe((user: User) => {
        if (user?.email) {
          this.form.get('email').setValue(user.email);
        }
      }),
    );

    this.subscription.add(
      this.checkoutValidationService.getErrors(ValidationErrorKeys.CONTACTS_REQUIRED).subscribe((errors) => {
        if (errors) {
          this.form.markAllAsTouched();
          this.cdr.markForCheck();
        }
      }),
    );

    this.subscription.add(
      this.checkoutValidationService.getScrollToEvent(ValidationErrorKeys.CONTACTS_REQUIRED).subscribe(() => {
        this.scrollToService.scrollToElement(this.viewRef.element.nativeElement, { offset: -50 });
      }),
    );

    this.customCheckoutService.clearPlaceOrderState();
  }

  connectToFormService(): void {
    this.subscription.add(this.formService.submitEvent.subscribe(() => this.onSubmit()));
    this.subscription.add(this.form.valueChanges.subscribe(this.formService.formValues$));
    this.subscription.add(
      this.form.statusChanges.subscribe(() => {
        this.formService.isValid$.next(this.form.valid);
      }),
    );
  }

  onSubmit(): void {
    this.checkoutValidationService
      .validate()
      .pipe(filter((errors) => !errors))
      .subscribe(() => {
        this.formService.markLoading();
        this.customCheckoutService.placeOrder(true);
      });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
